import { PolfEventModeEnum } from '@polf/shared/codegen/types'
import { formatChips } from '@polf/shared/locale'
import { useMemo } from 'react'
import { Link, useOutletContext, useParams } from 'react-router-dom'

import {
  type ProfilePlayerFragment,
  useCurrentPlayerFlightScoresQuery,
  useCurrentPlayerProfileQuery,
} from '../__generated__/hooks'
import { IconCard, IconLeaderboard, IconTarget } from '../components/Icon'
import type { CurrentPlayerCtx, Phase } from '../PlayerShell'
import { usesChips } from '../util/event-mode'
import { initialsOf } from '../util/initials'
import { getHoleStrokes } from '../util/scoring'

/**
 * Player profile — hero card, strokes-per-hole map, round info, chip rules.
 * Team-aware: Scramble shows team-only (team strokes, team stroke-map,
 * roster); BestBall shows player-level data + a team card listing members;
 * Golf / POLF show individual data (unchanged).
 *
 * Deferred (no API support):
 *   - QR code for poker-phase payouts
 *   - Avatar upload / photo
 */

const fmtTime = (input: string | null | undefined): string => {
  if (!input) return '—'
  // startTime is a MySQL `time` column — arrives as "HH:MM:SS", which
  // new Date() can't parse. Show HH:MM.
  const hhmmss = input.match(/^(\d{2}:\d{2})(?::\d{2})?$/)
  if (hhmmss) return hhmmss[1]
  const d = new Date(input)
  return Number.isNaN(d.getTime())
    ? input
    : d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })
}

const StatusPill = ({
  submitted,
  approved,
}: {
  submitted: boolean
  approved: boolean
}) => {
  if (approved) {
    return (
      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-green-700 text-paper">
        Godkjent
      </span>
    )
  }
  if (submitted) {
    return (
      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-green-100 text-green-800">
        Levert
      </span>
    )
  }
  return (
    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-cream-card text-ink-500">
      Pågår
    </span>
  )
}

/**
 * Hero card. Subtitle adapts to the team format:
 *  - Scramble: team name + team strokes (player hcp is irrelevant)
 *  - everything else: player hcp + individual strokes
 */
const Hero = ({
  profile,
  isScramble,
}: {
  profile: ProfilePlayerFragment
  isScramble: boolean
}) => {
  const { team } = profile
  const displayName = isScramble && team?.name ? team.name : profile.name
  const subtitle = isScramble ? (
    <>
      <span className="font-mono num">{team?.strokes ?? '—'}</span> tildelte
      slag
    </>
  ) : (
    <>
      HCP <span className="font-mono num">{profile.hcp ?? '—'}</span> ·{' '}
      <span className="font-mono num">{profile.strokes}</span> tildelte slag
    </>
  )
  return (
    <div className="mx-2 my-3 bg-green-900 text-paper rounded-lg p-5 flex flex-col gap-3 shadow">
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-paper text-green-900 flex items-center justify-center text-lg font-bold shrink-0">
          {initialsOf(displayName)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-xl truncate">
            {displayName}
          </div>
          <div className="text-sm text-green-100">{subtitle}</div>
        </div>
        <StatusPill
          submitted={profile.scoresSubmitted}
          approved={profile.scoresApproved}
        />
      </div>
      {/* No flight number here — players read "Flight 3" as their start hole.
          The number carries no player-facing meaning; start hole + time do. */}
      <div className="grid grid-cols-3 gap-3 text-center border-t border-green-800 pt-3">
        <div>
          <div className="text-[10px] up text-green-100">Starthull</div>
          <div className="font-display font-bold text-lg">
            {profile.flight?.startHole?.number ?? '—'}
          </div>
        </div>
        <div>
          <div className="text-[10px] up text-green-100">Starttid</div>
          <div className="font-display font-bold text-lg num">
            {profile.flight?.startTime
              ? fmtTime(profile.flight.startTime)
              : '—'}
          </div>
        </div>
        <div>
          <div className="text-[10px] up text-green-100">Utslagssted</div>
          <div className="font-display font-bold text-lg">
            {profile.courseTee?.teeName ?? '—'}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Scramble multiplier table per player-count (Norges Golfforbund / USGA).
 * Sorted by hcp ascending — lowest hcp gets the largest weighting.
 * Order MUST match the API's `PolfEventTeam.strokes` getter so the
 * explanation lines up with the canonical total.
 */
const SCRAMBLE_PERCENTAGES: Record<number, number[]> = {
  1: [1],
  2: [0.35, 0.15],
  3: [0.2, 0.15, 0.1],
  4: [0.2, 0.15, 0.1, 0.05],
}

/**
 * Team roster card. Shown on Scramble + BestBall events.
 *  - Scramble: shows each member's weighting (20 / 15 / 10 / 5 % depending
 *    on team size) sorted by hcp asc, then a sum row with the team's
 *    canonical total (computed server-side, accounts for slope/rating).
 *  - BestBall: plain roster (no weighting concept).
 */
const TeamCard = ({
  team,
  isScramble,
}: {
  team: NonNullable<ProfilePlayerFragment['team']>
  isScramble: boolean
}) => {
  const allPlayers = team.players ?? []
  // Sort by hcp asc so A (lowest hcp) gets the biggest multiplier. Players
  // missing an hcp drop to the bottom — they wouldn't be valid for a
  // Scramble strokes calc anyway.
  const sortedPlayers = [...allPlayers].sort(
    (a, b) =>
      (a.hcp ?? Number.POSITIVE_INFINITY) - (b.hcp ?? Number.POSITIVE_INFINITY)
  )
  const pctList: number[] | undefined = isScramble
    ? SCRAMBLE_PERCENTAGES[sortedPlayers.length]
    : undefined

  return (
    <div className="mx-2 my-3 bg-paper rounded-lg p-4 shadow flex flex-col gap-2">
      <div className="up text-ink-500">Laget</div>
      <div className="font-display font-bold text-lg text-ink-900 truncate">
        {team.name ?? 'Uten navn'}
      </div>
      {isScramble ? (
        <p className="text-[11px] text-ink-500">
          Vektet hcp: lavest hcp får størst andel av lagets handicap.
        </p>
      ) : null}
      <ul className="flex flex-col mt-1">
        {sortedPlayers.map((p, i) => {
          const pct = pctList?.[i]
          const contribution = pct != null && p.hcp != null ? p.hcp * pct : null
          return (
            <li
              key={p.id}
              className="flex items-center justify-between text-sm py-1 border-t border-ink-100 first:border-t-0"
            >
              <span className="font-semibold text-ink-900 truncate">
                {p.name}
              </span>
              <span className="flex items-center gap-2 shrink-0 font-mono num">
                <span className="text-ink-500">HCP {p.hcp ?? '—'}</span>
                {pct != null ? (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full">
                    {Math.round(pct * 100)} %
                  </span>
                ) : null}
                {contribution != null ? (
                  <span className="text-ink-700">
                    = {contribution.toFixed(1)}
                  </span>
                ) : null}
              </span>
            </li>
          )
        })}
        {isScramble && team.strokes != null ? (
          <>
            {(() => {
              const weightedHcp = sortedPlayers.reduce((acc, p, i) => {
                const pct = pctList?.[i]
                return acc + (pct != null && p.hcp != null ? p.hcp * pct : 0)
              }, 0)
              return (
                <li className="flex items-center justify-between text-sm py-1.5 border-t border-ink-100 text-ink-700">
                  <span>Vektet hcp</span>
                  <span className="font-mono num font-semibold">
                    {weightedHcp.toFixed(1)}
                  </span>
                </li>
              )
            })()}
            <li className="flex items-center justify-between text-sm py-2 border-t-2 border-ink-200 font-semibold">
              <span className="text-ink-900">Tildelte slag</span>
              <span className="font-mono num font-bold text-ink-900">
                {team.strokes}
              </span>
            </li>
            <li className="text-[11px] text-ink-500 pt-1">
              Tildelte slag er justert for slope og course rating.
            </li>
          </>
        ) : null}
      </ul>
    </div>
  )
}

/**
 * Strokes-per-hole map. Each cell shows the hole number with up to N
 * red dots underneath representing strokes given.
 *
 *  - Scramble: team strokes (per-hole distribution of the team's total).
 *    The flight-scores entry for the player's team has `strokes` already
 *    resolved per hole via the score entity's getter.
 *  - BestBall / Golf / POLF: the player's individual strokes.
 *
 * Reads from currentPlayerFlightScores (already in Apollo cache from the
 * Score / Scorecard pages in most flows).
 */
const StrokesMap = ({
  playerId,
  teamId,
}: {
  playerId: string
  teamId?: string | null
}) => {
  const { data } = useCurrentPlayerFlightScoresQuery({
    fetchPolicy: 'cache-first',
  })
  const flight = data?.currentPlayerFlightScores ?? []
  // Scramble entries are keyed by team id; everything else by player id.
  // Prefer player match (individual strokes) when present; fall back to
  // the team entry for Scramble.
  const yourEntry =
    flight.find((e) => e.id === playerId) ??
    (teamId ? flight.find((e) => e.id === teamId) : undefined)
  const totalStrokes = yourEntry?.strokes ?? 0
  const holes = useMemo(() => {
    return (yourEntry?.scores ?? [])
      .slice()
      .sort((a, b) => a.hole.number - b.hole.number)
  }, [yourEntry])

  if (!holes.length) return null

  return (
    <div className="mx-2 my-3 bg-paper rounded-lg p-4 shadow">
      <div className="up text-ink-500 mb-3">
        {teamId ? 'Lagets tildelte slag' : 'Tildelte slag'}
      </div>
      <div className="grid grid-cols-9 gap-1.5">
        {holes.map((s) => {
          // The score-row `strokes` getter on the API returns 0 for
          // Scramble team scores (team.polfEvent isn't loaded on
          // score.team). Recompute client-side from the entry's total —
          // same algorithm as the server, lives in util/scoring.
          const n =
            totalStrokes > 0 ? getHoleStrokes(totalStrokes, s.hole.index) : 0
          const dotColor = n === 0 ? 'bg-ink-200' : 'bg-flag'
          return (
            <div
              key={s.hole.number}
              className={`flex flex-col items-center gap-0.5 py-1.5 rounded ${
                n > 0 ? 'bg-cream-card' : ''
              }`}
            >
              <div className="font-mono num text-[11px] text-ink-700 font-semibold">
                {s.hole.number}
              </div>
              <div className="flex items-center gap-0.5 h-1.5">
                {Array.from({ length: Math.max(1, n) }).map((_, i) => (
                  <span
                    // Decorative dots — index is the stable key.
                    // eslint-disable-next-line react/no-array-index-key
                    key={`dot-${s.hole.number}-${i}`}
                    className={`w-1 h-1 rounded-full ${dotColor}`}
                  />
                ))}
              </div>
            </div>
          )
        })}
      </div>
      <p className="text-[11px] text-ink-500 mt-3">
        Røde prikker = tildelte slag fra hcp. Grå = ingen tildelte slag.
      </p>
    </div>
  )
}

const ChipRules = ({
  chipValues,
}: {
  chipValues: ProfilePlayerFragment['polfEvent']['chipValues']
}) => {
  const rules: { label: string; value: number; suffix?: string }[] = [
    { label: 'Startstack', value: chipValues.startStack, suffix: 'chips' },
    {
      label: 'Stableford-poeng',
      value: chipValues.stablefordPoint,
      suffix: 'chips',
    },
    { label: 'Birdie-bonus', value: chipValues.birdie, suffix: 'chips' },
    { label: 'Eagle-bonus', value: chipValues.eagle, suffix: 'chips' },
    { label: 'HiO-bonus', value: chipValues.holeInOne, suffix: 'chips' },
  ]
  if (chipValues.flightBonusEnabled) {
    rules.push({
      label: 'Flight-bonus',
      value: chipValues.flightBonus,
      suffix: 'chips',
    })
  }
  return (
    <div className="mx-2 my-3 bg-paper rounded-lg shadow overflow-hidden">
      <span className="up text-ink-500 w-full px-4 py-3 flex items-center justify-between text-left">
        Chip-regler
      </span>

      <div className="px-4 pb-4">
        <dl className="flex flex-col gap-2 text-sm">
          {rules.map((r) => (
            <div key={r.label} className="flex items-center justify-between">
              <dt className="text-ink-700">{r.label}</dt>
              <dd className="font-mono num font-bold text-ink-900">
                {formatChips(r.value)} {r.suffix}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  )
}

/**
 * Round archive — links to the read-only golf surfaces. Shown ONLY in
 * POKER phase, since the GOLF-phase bottom nav already exposes Scorecard,
 * Topp, and Sidespill as primary tabs. Once poker phase takes over, those
 * tabs vanish from the rail; this card is their new home.
 *
 * Sidegames link is always rendered — the sidegames page handles its own
 * empty state if the event has no closest/longest comps, and we'd need an
 * extra query field to gate it here.
 */
const RoundArchive = ({ playerKey }: { playerKey: string }) => {
  const items: {
    to: string
    label: string
    sub: string
    Icon: typeof IconCard
  }[] = [
    {
      to: `/s/${playerKey}/scorecard`,
      label: 'Scorekort',
      sub: 'Rundens hull-for-hull',
      Icon: IconCard,
    },
    {
      to: `/s/${playerKey}/leaderboard`,
      label: 'Leaderbord fra golf',
      sub: 'Topplisten fra runden',
      Icon: IconLeaderboard,
    },
    {
      to: `/s/${playerKey}/sidegames`,
      label: 'Sidespill',
      sub: 'Closest og longest',
      Icon: IconTarget,
    },
  ]
  return (
    <div className="mx-2 my-3 bg-paper rounded-lg shadow overflow-hidden">
      <div className="px-4 py-2 border-b border-ink-100">
        <span className="up text-ink-500">Min runde</span>
      </div>
      <ul>
        {items.map(({ to, label, sub, Icon }) => (
          <li key={to}>
            <Link
              to={to}
              className="flex items-center gap-3 px-4 py-3 border-b border-ink-100 last:border-b-0 active:opacity-70"
            >
              <span className="w-9 h-9 rounded-full bg-cream-card flex items-center justify-center text-green-800 shrink-0">
                <Icon size={18} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold text-ink-900">
                  {label}
                </span>
                <span className="block text-[11px] text-ink-500">{sub}</span>
              </span>
              <span aria-hidden className="text-ink-300 text-lg">
                ›
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

const Profile = () => {
  const { player, phase } = useOutletContext<{
    player: CurrentPlayerCtx
    phase: Phase
  }>()
  const { playerKey = '' } = useParams<{ playerKey: string }>()
  const { data, loading, error } = useCurrentPlayerProfileQuery({
    fetchPolicy: 'cache-and-network',
  })

  if (loading && !data) {
    return (
      <section className="px-4 py-8 text-center text-ink-500">
        Laster profil…
      </section>
    )
  }
  if (error || !data?.currentPlayer) {
    return (
      <section className="px-4 py-8 text-center text-flag">
        Kunne ikke laste profilen.
      </section>
    )
  }

  const profile = data.currentPlayer
  const { mode } = profile.polfEvent
  const isScramble = mode === PolfEventModeEnum.Scramble
  const isBestBall = mode === PolfEventModeEnum.Bestball
  const showTeamCard = (isScramble || isBestBall) && profile.team != null

  return (
    <section className="pb-4">
      <Hero profile={profile} isScramble={isScramble} />
      {showTeamCard && profile.team ? (
        <TeamCard team={profile.team} isScramble={isScramble} />
      ) : null}
      <StrokesMap
        playerId={player.id}
        teamId={isScramble ? profile.team?.id : null}
      />
      {usesChips(mode) ? (
        <ChipRules chipValues={profile.polfEvent.chipValues} />
      ) : null}
      {phase === 'POKER' ? <RoundArchive playerKey={playerKey} /> : null}
    </section>
  )
}

export default Profile
